import React, { useState } from "react";
import {
  View,
  StyleSheet,
  Platform,
  TouchableOpacity,
  Text,
  ActivityIndicator,
} from "react-native";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../../store";
import { createApiClient, apiClient ,getLocalTimestamp} from "../../../api/apiClient";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useToast } from "../../../components/LhToastContainer";
import {
  setWalkId,
  setSuccessMessage,
  setSelectedZone,
  setSelectedLeadershipWalk,
  selectSelectedFrequency,
} from "../../../store/slices/leaderShipWalkSlice";
import { fetchLeadershipWalks } from "../utils/fetchLeadershipWalks";
import { resetLeadershipWalkRedirect } from "../../../store/slices/leadershipWalkRedirectSlice";
import { handleLeadershipWalkErrorWithFriendlyMessage, LEADERSHIP_WALK_API_NAMES } from '../../../store/utils/leadershipWalkErrorHandler';
import { useTheme } from "../../../theme";

// Define __DEV__ if it doesn't exist (for web environments)
const __DEV__: boolean =
  typeof (globalThis as any).__DEV__ !== "undefined"
    ? (globalThis as any).__DEV__
    : process.env.NODE_ENV !== "production";

interface WalkData {
  location: string;
  walkType: string;
  zone: string;
  frequency: any;
  period?: number;
  week?: number;
  miscellImageDesc?: string;
  miscellImage2Desc?: string;
  managerSignature: string;
  miscellImage?: string;
  miscellImage2?: string;
  updatedImg1?: boolean;
  updatedImg2?: boolean;
  updatedManagerSign?: boolean;
  status: string;
  createdBy: string;
  // Add field for miscellaneous observations text
  miscellaneousObservations?: string;
}

interface LeadershipImage {
  ImageId: string;
  ImageBase64: string;
  Description: string;
  ObservationId: string;
}

interface LeadershipWalkPayload {
  oneSubmission?: boolean;
  selectedRecord?: {
    id: number;
  };
  walkData: WalkData;
  toolsMapping: {
    toolId: number;
  }[];
  leadershipImages?: LeadershipImage[];
  // Additional fields for tracking creation/modification
  created?: string;
  createdBy?: string;
  modified?: string;
  modifiedBy?: string;
}

interface LeadershipWalkRedirectActionProps {
  onSave: () => void;
  onComplete: () => boolean;
  onBackToObservation?: () => void;
  forceDisabled?: boolean;
}

const LeadershipWalkRedirectAction: React.FC<
  LeadershipWalkRedirectActionProps
> = ({ onSave, onComplete, onBackToObservation, forceDisabled = false }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === "dark";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPdfExporting, setIsPdfExporting] = useState(false);
  const dispatch = useDispatch();
  const toast = useToast();

  // Get data from Redux state
  const selectedOptions = useSelector(
    (state: RootState) => state.leadershipWalkRedirect.selectedOptions
  );

  const walkId = useSelector((state: RootState) => state.leadershipWalk.walkId);

  const walkState = useSelector(
    (state: RootState) => state.leadershipWalkRedirect.walkState
  );

  // Replace the existing selectedFilters selector with:
  const selectedZones = useSelector(
    (state: RootState) => state.leadershipWalk.selectedZone
  );

  const selectedFrequency = useSelector(selectSelectedFrequency);

  const selectedLocation = useSelector(
    (state: RootState) => state.appData.selectedLocation
  );

  const period = useSelector(
    (state: RootState) => state.appData.periodWeek?.data?.period
  );

  const week = useSelector(
    (state: RootState) => state.appData.periodWeek?.data?.week
  );

  const userEmail = useSelector(
    (state: RootState) => state.auth.user?.username
  );

  const selectedRecord = useSelector(
    (state: RootState) => state.leadershipWalkRedirect.selectedRecord
  );
  const selectedRecordId = selectedRecord?.id || "";

  // Get images from Redux store
  const miscObservationImages = useSelector(
    (state: RootState) =>
      state.leadershipWalkRedirect.miscObservationImages || []
  );

  const leadershipImages = useSelector(
    (state: RootState) => state.leadershipWalkRedirect.images || []
  );
  // Get miscellaneous observations text
  const miscellaneousObservations = useSelector(
    (state: RootState) =>
      state.leadershipWalkRedirect.miscellaneousObservations || ""
  );

  // Get manager signature data from Redux state
  const managerSignature = useSelector((state: RootState) => ({
    fileName: state.leadershipWalkRedirect.managerSignatureFileName,
    imageUrl: state.leadershipWalkRedirect.managerSignatureImageUrl,
    isUpdated: state.leadershipWalkRedirect.isManagerSignatureUpdated,
  }));

  // Function to handle PDF export via email
  const handlePdfExport = async () => {
    if (isPdfExportDisabled || !selectedRecordId || !userEmail) {
      toast.showToast({
        header: "PDF Export Error",
        subtext: "Walk must be saved before exporting PDF.",
        variant: "error",
      });
      return;
    }

    try {
      setIsPdfExporting(true);

      // Build the subject line
      const recordName =
        selectedRecord?.name ||
        (selectedZones.length > 0
          ? selectedZones.map((zone) => zone.ZONE_ID).join(", ")
          : "Unknown");
      const subjectLine = `Leadership Walk Report for ${recordName} - ${selectedRecordId}`;

      // Build the API URL with query parameters
      const api = createApiClient();
      const response = await apiClient(
        api,
        `/notifications/email/pdf?emailTo=${encodeURIComponent(
          userEmail
        )}&inspection_module=leadership_walk&id=${selectedRecordId}&subject=${encodeURIComponent(
          subjectLine
        )}`,
        "GET"
      );

      // Handle the response
      if (response && response.message) {
        toast.showToast({
          header: "Email Notification",
          subtext: response.message,
          variant: response.messageId ? "success" : "error",
        });
        console.log("PDF export success:", response.message);
      } else {
        toast.showToast({
          header: "Email Error",
          subtext: "Failed to send PDF export email",
          variant: "error",
        });
        throw new Error("Invalid response from server");
      }

      console.log("API response:", response);
    } catch (error: any) {
      console.error("PDF Export Error:", error);
      toast.showToast({
        header: "Email Error",
        subtext: error?.message || "Failed to export PDF",
        variant: "error",
      });
    } finally {
      setIsPdfExporting(false);
    }
  };

  const handleSubmit = async (action: "save" | "complete") => {
    try {
      // Only validate for complete action
      if (action === "complete") {
        console.log("Calling validation...");
        const isValid = onComplete();
        console.log("Validation result:", isValid);

        if (!isValid) {
          console.log("Validation failed, exiting");
          return;
        }
      } else {
        // For save action, call onSave
        onSave();
      }

      // Set submitting state to true to disable both buttons
      setIsSubmitting(true);

      console.log(`Submitting leadership walk (${action})...`);

      // Log all the required data for the payload with detailed image info
      console.log("Preparing payload with:", {
        selectedZones,
        selectedOptions,
        walkState,
        miscObservationImages,
        miscellaneousObservations,
        managerSignature,
        location: selectedLocation,
      });

      // Debug image data specifically
      console.log("Debug image data:", {
        totalImages: miscObservationImages.length,
        firstImage: miscObservationImages[0]
          ? {
              hasImageUrl: !!miscObservationImages[0].imageUrl,
              imageUrlLength: miscObservationImages[0].imageUrl?.length || 0,
              index: miscObservationImages[0].index,
            }
          : "No images",
      });

      // Prepare the API request payload
      // Extract employee info from email
      let firstName = "";
      let lastName = "";
      let gpid = "";
      if (userEmail) {
        const nameParts = userEmail.split("@")[0].split(".");
        if (nameParts.length >= 2) {
          firstName = nameParts[0];
          lastName = nameParts[1];
          // Generate GPID from email assuming first letter of first name + last name
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          gpid = (firstName[0] + lastName).toLowerCase();
        }
      }

      // Prepare the API request payload
      const payload: LeadershipWalkPayload = {
        walkData: {
          location:
            selectedLocation?.name || selectedLocation?.displayText || "",
          walkType: "Leadership Walk", // Always "Leadership Walk"
          zone: selectedZones.map((zone) => zone.ZONE_ID).join(","),
          frequency: selectedFrequency,
          period: period ? parseInt(String(period), 10) : undefined,
          week: week ? parseInt(String(week), 10) : undefined,
          miscellImageDesc:
            miscObservationImages.find((img) => img.index === 0)?.description ||
            "",
          miscellImage2Desc:
            miscObservationImages.find((img) => img.index === 1)?.description ||
            "",
          managerSignature: managerSignature.fileName || "",
          miscellImage:
            miscObservationImages.find((img) => img.index === 0)?.imageUrl ||
            "",
          miscellImage2:
            miscObservationImages.find((img) => img.index === 1)?.imageUrl ||
            "",
          updatedImg1: Boolean(
            miscObservationImages.find((img) => img.index === 0)?.imageUrl
          ),
          updatedImg2: Boolean(
            miscObservationImages.find((img) => img.index === 1)?.imageUrl
          ),
          updatedManagerSign: managerSignature.isUpdated,
          status: action === "save" ? "In progress" : "Completed",
          createdBy: userEmail || "",
          miscellaneousObservations: miscellaneousObservations,
        },
        toolsMapping: selectedOptions.map((option) => ({
          toolId: parseInt(option.id, 10) || 0,
        })),
      };

      // This is the key line - if there's an existing record ID OR the walk is 'in_progress',
      // then we should set oneSubmission=true to update the existing record instead of creating a new one
      const existingRecordId = walkId || selectedRecordId;
      const isExistingRecord =
        Boolean(existingRecordId) || walkState === "in_progress";

      console.log(
        `Walk status check: walkState=${walkState}, action=${action}, isExistingRecord=${isExistingRecord}, existingRecordId=${
          existingRecordId || "none"
        }`
      );

      // For a new leadership walk with no existing ID or record
      if (walkState === "not_started") {
        payload.oneSubmission = false;
        payload.created = getLocalTimestamp()
        payload.createdBy = userEmail || "";
        payload.modifiedBy = userEmail || "";
        payload.modified = getLocalTimestamp()
        console.log("Creating new leadership walk (oneSubmission=false)");
      } else if (isExistingRecord || selectedRecord?.id) {
        payload.modifiedBy = userEmail || "";
        payload.modified = getLocalTimestamp()
        payload.oneSubmission = true;
        let recordId = 0;
        if (
          existingRecordId &&
          !isNaN(parseInt(String(existingRecordId), 10))
        ) {
          recordId = parseInt(String(existingRecordId), 10);
        } else if (
          selectedRecord &&
          selectedRecord.id &&
          !isNaN(parseInt(String(selectedRecord.id), 10))
        ) {
          recordId = parseInt(String(selectedRecord.id), 10);
        }
        console.log(
          `ID resolution: walkId=${walkId}, selectedRecordId=${selectedRecordId}, selectedRecord.id=${selectedRecord?.id}, final recordId=${recordId}`
        );
        payload.selectedRecord = {
          id: recordId,
        };
        console.log(
          `Updating existing walk: ID=${payload.selectedRecord.id}, oneSubmission=true`
        );
      }
      console.log("leadership walk payload:", leadershipImages);
      // Add leadership images to the payload
      if (leadershipImages && leadershipImages.length > 0) {
        payload.leadershipImages = leadershipImages.map((img) => ({
          ImageId: String(img.imageId), // Use index as the ImageId
          ImageBase64: img.imageUrl || img.imagePreview || "",
          Description: img.description || "",
          ObservationId: String(img.observationId), // Use index as the ObservationId
        }));
      }

      // Log payload for debugging in development
      if (__DEV__) {
        const payloadForLogging = JSON.parse(JSON.stringify(payload));
        if (payloadForLogging.leadershipImages) {
          payloadForLogging.leadershipImages =
            payloadForLogging.leadershipImages.map((img: any) => ({
              ...img,
              ImageBase64: img.ImageBase64
                ? `[Binary data of length ${img.ImageBase64.length}]`
                : null,
            }));
        }
        console.log("Leadership Walk Submit Payload:", payloadForLogging);
      }

      console.log("Making API call...", payload);

      // Make the API call
      const api = createApiClient();

      console.log(
        `Making API call to /leadership-walks/submit with action=${action}, walkState=${walkState}, recordId=${payload.selectedRecord?.id}`
      );

      const response = await apiClient(
        api,
        "/leadership-walks/submit",
        "POST",
        payload
      );

      console.log("API response:", response);

      // Handle the API response
      if (response) {
        // Check for walkId in the response and update Redux state
        if (typeof response.walkId === "number") {
          console.log("Setting walkId in Redux:", response.walkId);
          dispatch(setWalkId(response.walkId));
        } else {
          console.warn("No walkId in response:", response);
        }

        // Set success message based on action type
        const successMessage =
          action === "save"
            ? "Leadership Walk saved successfully."
            : "Leadership Walk completed successfully.";
        dispatch(setSuccessMessage(successMessage));

        console.log(
          `Leadership Walk ${
            action === "save" ? "saved" : "submitted"
          } successfully.`
        );

        // Reset the form data
        dispatch(resetLeadershipWalkRedirect());
        // Also reset the selectedZone and selectedLeadershipWalk in leadershipWalkSlice
        dispatch(setSelectedZone([]));
        dispatch(setSelectedLeadershipWalk(null));

        // Navigate back to main screen
        if (onBackToObservation) {
          console.log("Navigating back to main screen");
          onBackToObservation();
        }

        // Refresh data in the background
        try {
          if (selectedLocation?.name || selectedLocation?.displayText) {
            console.log("Fetching updated leadership walks...");
            fetchLeadershipWalks({
              location:
                selectedLocation?.name || selectedLocation?.displayText || "",
              dispatch,
            })
              .then(() => {
                console.log("Leadership walks data refreshed successfully");
              })
              .catch((err) => {
                console.error("Error refreshing data (non-critical):", err);
              });
          }
        } catch (err) {
          console.error("Error starting data refresh (non-critical):", err);
        }
      } else {
        // Show centralized overlay with a friendly message, then throw to be handled by catch
        const overlayMsg = response?.message || `Unable to ${action === 'save' ? 'save' : 'submit'} leadership walk. Please try again.`;
        try {
          handleLeadershipWalkErrorWithFriendlyMessage(
            LEADERSHIP_WALK_API_NAMES.SUBMIT_WALK,
            overlayMsg,
            true,
            { response, action }
          );
        } catch (e) {
          console.error('Failed to show centralized leadership walk overlay:', e);
        }

        throw new Error(overlayMsg);
      }
    } catch (error: any) {
      console.error("Submit Error:", error);

      // Show centralized overlay for caught exceptions as well
      try {
        const overlayMsg =
          error?.response?.data?.message || error?.message || "Unable to submit leadership walk. Please try again.";
        handleLeadershipWalkErrorWithFriendlyMessage(
          LEADERSHIP_WALK_API_NAMES.SUBMIT_WALK,
          overlayMsg,
          true,
          { originalError: error }
        );
      } catch (e) {
        console.error('Failed to dispatch leadership walk error overlay in catch:', e);
      }

      // If it's a complete action with error, don't reset the data so user can fix and retry
      // If it's just a save action with error, we can still go back and reset
      if (action === "save") {
        dispatch(resetLeadershipWalkRedirect());
        // Reset only the selectedLeadershipWalk but preserve the selectedZone
        dispatch(setSelectedLeadershipWalk(null));

        if (onBackToObservation) {
          console.log("Navigating back after save action error");
          onBackToObservation();
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Determine if the PDF export should be disabled
  const isPdfExportDisabled =
    forceDisabled ||
    walkState === "not_started" ||
    walkState === "in_progress" ||
    !walkId;

  return (
    <View
      style={[
        styles.actionRow,
        isDarkMode && { borderTopColor: colors.border.default },
      ]}
    >
      <View style={styles.componentWrapper}>
        {/* PDF Export button */}
        <TouchableOpacity
          style={[
            styles.customButton,
            styles.pdfExportButton,
            isDarkMode && { backgroundColor: colors.background.surface, borderColor: colors.border.default, borderWidth: 1 },
            (isPdfExportDisabled || isPdfExporting) && styles.disabledButton,
          ]}
          onPress={handlePdfExport}
          disabled={isPdfExportDisabled || isPdfExporting}
        >
          <View style={styles.buttonContent}>
            <MaterialIcons
              name="picture-as-pdf"
              size={20}
              color={isDarkMode ? colors.text.inverse : "white"}
              style={styles.icon}
            />
            <Text
              style={[
                styles.buttonText,
                isDarkMode && { color: colors.text.inverse },
                (isPdfExportDisabled || isPdfExporting) &&
                  styles.buttonTextDisabled,
              ]}
            >
              {isPdfExporting ? "Sending..." : "Export as PDF"}
            </Text>
          </View>
        </TouchableOpacity>
      </View>

      <View style={styles.buttonColumn}>
        {/* Save for Later Button */}
        <TouchableOpacity
          style={[
            styles.customButton,
            styles.buttonFlex,
            isSubmitting || walkState === "completed"
              ? styles.disabledButton
              : styles.saveButton,
            isDarkMode && !(isSubmitting || walkState === "completed")
              ? { backgroundColor: "#000000", borderColor: "#005CBC", borderWidth: 1 }
              : null,
            Platform.OS === "web" &&
            !(isSubmitting || walkState === "completed")
              ? { cursor: "pointer" as any }
              : {},
          ]}
          onPress={() => handleSubmit("save")}
          disabled={forceDisabled || isSubmitting || walkState === "completed"}
          accessibilityState={{
            disabled:
              forceDisabled || isSubmitting || walkState === "completed",
          }}
          accessibilityLabel={
            isSubmitting
              ? "Save for later (disabled during submission)"
              : walkState === "completed"
              ? "Save for later (disabled for completed walk)"
              : "Save for later"
          }
        >
          <View style={styles.buttonContent}>
            {isSubmitting ? (
              <ActivityIndicator
                size="small"
                color={isDarkMode ? colors.text.inverse : "#2D2D2D"}
                style={styles.icon}
              />
            ) : (
              <MaterialIcons
                name="save"
                size={20}
                color={isDarkMode ? colors.text.inverse : "#2D2D2D"}
                style={styles.icon}
              />
            )}
            <Text
              style={[
                styles.buttonText,
                isDarkMode ? { color: "#FFFFFF" } : { color: "#2D2D2D" },
                isSubmitting && styles.buttonTextDisabled,
              ]}
            >
              {isSubmitting ? "Please wait..." : "Save for later"}
            </Text>
          </View>
        </TouchableOpacity>

        {/* Complete Button */}
        <TouchableOpacity
          style={[
            styles.customButton,
            styles.buttonFlex,
            isSubmitting || walkState === "completed"
              ? styles.disabledButton
              : styles.completeButton,
            isDarkMode && !(isSubmitting || walkState === "completed")
              ? { backgroundColor: colors.brand.primary }
              : null,
            Platform.OS === "web" &&
            !(isSubmitting || walkState === "completed")
              ? { cursor: "pointer" as any }
              : {},
          ]}
          onPress={() => {
            console.log("Complete button pressed, walkState:", walkState);
            handleSubmit("complete");
          }}
          disabled={forceDisabled || isSubmitting || walkState === "completed"}
          accessibilityState={{
            disabled:
              forceDisabled || isSubmitting || walkState === "completed",
          }}
          accessibilityLabel={
            isSubmitting
              ? "Complete form (disabled during submission)"
              : walkState === "completed"
              ? "Complete form (disabled for completed walk)"
              : walkState === "in_progress"
              ? "Complete in-progress walk"
              : "Complete form"
          }
        >
          <View style={styles.buttonContent}>
            {isSubmitting ? (
              <ActivityIndicator
                size="small"
                color={colors.text.inverse}
                style={styles.icon}
              />
            ) : (
              <MaterialIcons
                name="check-circle"
                size={20}
                color={colors.text.inverse}
                style={styles.icon}
              />
            )}
            <Text
              style={[
                styles.buttonText,
                isSubmitting && styles.buttonTextDisabled,
              ]}
            >
              {isSubmitting ? "Submitting..." : "Complete"}
            </Text>
          </View>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  actionRow: {
    flexDirection: Platform.OS === "web" ? "row" : "column",
    flexWrap: "wrap",
    marginTop: 30,
    borderTopWidth: 1,
    borderTopColor: "#dee2e6",
    paddingTop: 24,
    gap: 20,
    alignItems: "center",
    width: "100%",
  },
  errorContainer: {
    backgroundColor: "#ffdddd",
    padding: 12,
    borderRadius: 6,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "#ff8888",
  },
  errorText: {
    color: "#d32f2f",
    fontSize: 14,
    textAlign: "center",
  },
  componentWrapper: {
    flex: 1,
    marginRight: 10,
    minWidth: 0,
  },
  buttonColumn: {
    flex: 2,
    flexDirection: "row",
    gap: 10,
    minWidth: 0,
    width: Platform.OS === "web" ? "auto" : "100%",
  },
  buttonFlex: {
    flex: 1,
    minWidth: 0,
  },
  customButton: {
    paddingVertical: 14,
    paddingHorizontal: 16,
    borderRadius: 6,
    backgroundColor: "#ccc",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  saveButton: {
    backgroundColor: "transparent",
    borderWidth: 2,
    borderColor: "#2D2D2D",
  },
  completeButton: {
    backgroundColor: "#008242",
  },
  pdfExportButton: {
    backgroundColor: "#D32F2F",
  },
  disabledButton: {
    opacity: 0.7,
    backgroundColor: "#999",
    borderWidth: 1,
    borderColor: "#ccc",
    shadowOpacity: 0.1,
    ...(Platform.OS === "web" ? { cursor: "not-allowed" as any } : {}),
  },
  buttonContent: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
  },
  icon: {
    marginRight: 8,
  } as any,
  buttonText: {
    color: "white",
    fontSize: 16,
    fontWeight: "600",
  },
  buttonTextDisabled: {
    color: "#DDD",
  },
  backButton: {
    backgroundColor: "#6c757d", // Gray color for back button
  },
});

export default LeadershipWalkRedirectAction;
